import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Share, Menu, Crown, Check, AlertTriangle, Download, Pencil } from 'lucide-react';
import * as awarenessProtocol from 'y-protocols/awareness';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch, useDemoStatus, getGravityUserPlan } from '../../hooks/useAuth';
import { useAuthStore } from '../../stores/authStore';
import { useDiagramAccess } from '../../hooks/useDiagramAccess';
import { ShareDialog } from '../modals/ShareDialog';
import { ExportControls } from '../export/ExportControls';
import { useDiagramStore } from '@/stores/diagramStore';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ConnectedUsers } from '../ConnectedUsers';
import { FolderSidebar } from './FolderSidebar';
import { useThemeMode } from '../../react-app/theme/ThemeProvider';
import { useDiagramDocument } from '../../contexts/DiagramDocumentContext';
import { useDiagramInfo, type DiagramInfo } from '../../hooks/useDiagramInfo';
import { OPEN_ONBOARDING_EVENT } from '../modals/OnboardingDialog';
import { useTranslation } from '../../hooks/useTranslation';
import { DiagramBreadcrumb } from '../navigation/DiagramBreadcrumb';
import { InfraViewBreadcrumbs } from '../../domains/network/components/InfraViewBreadcrumbs';
import { CompassBreadcrumb } from '../../domains/compass/components/CompassBreadcrumb';
import { getDiagramNameForDisplay } from '../../dashboard/utils/diagramHelpers';
import { useEntitlements } from '../../hooks/useEntitlements';
import { shouldShowUpgradeCta } from './floatingHeaderPlan';
import { useAtlasDepartmentViewTitle } from '../../domains/orgchart/hooks/useAtlasDepartmentViewTitle';
import { useAtlasInsightViewTitle } from '../../domains/orgchart/hooks/useAtlasInsightViewTitle';

interface FloatingHeaderProps {
  awareness?: awarenessProtocol.Awareness;
  offsetTop?: number;
}

type HeaderWindow = Window & { __DIAGRAM_ID__?: string };
type HeaderDiagram = Pick<DiagramInfo, 'id' | 'name'> & {
  folderId?: string | null;
  viewDefinition?: DiagramInfo['viewDefinition'];
};

const SyncStatusIndicator: React.FC<{
  isSyncing: boolean;
  pendingCount?: number;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  onDownloadBackup?: () => void;
}> = ({ isSyncing, pendingCount, saveStatus, onDownloadBackup }) => {
  const { t: tc } = useTranslation('common');
  const [showSaved, setShowSaved] = useState(false);
  const prevSyncingRef = useRef(false);

  useEffect(() => {
    if (prevSyncingRef.current && !isSyncing) {
      setShowSaved(true);
      const timer = setTimeout(() => setShowSaved(false), 3000);
      return () => clearTimeout(timer);
    }
    prevSyncingRef.current = isSyncing;
  }, [isSyncing]);

  if (isSyncing) {
    return (
      <div className="ml-3 flex items-center gap-1 text-xs text-muted-foreground">
        <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-blue-600" />
        <span>{tc('status.syncing', { count: Math.max(1, pendingCount ?? 1) })}</span>
      </div>
    );
  }

  if (saveStatus === 'error') {
    return (
      <div className="ml-3 flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1 text-xs text-amber-600 cursor-help">
              <AlertTriangle className="h-3 w-3" />
              <span>{tc('status.error')}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {tc('status.errorTooltip', { defaultValue: 'Changes could not be saved. Check your connection or export your work.' })}
          </TooltipContent>
        </Tooltip>
        {onDownloadBackup && (
          <button
            type="button"
            data-testid="tid.editor.header.downloadBackup"
            onClick={onDownloadBackup}
            className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 underline underline-offset-2"
          >
            <Download className="h-3 w-3" />
            {tc('status.downloadBackup', { defaultValue: 'Download backup' })}
          </button>
        )}
      </div>
    );
  }

  if (showSaved || saveStatus === 'saved') {
    return (
      <div className="ml-3 flex items-center gap-1 text-xs text-green-600 animate-in fade-in duration-300">
        <Check className="h-3 w-3" />
        <span>{tc('status.saved')}</span>
      </div>
    );
  }

  return null;
};

export const FloatingHeader: React.FC<FloatingHeaderProps> = ({
  awareness,
  offsetTop = 16,
}) => {
  const { isSyncing, pendingCount, saveStatus, canWrite, downloadBackup } = useDiagramDocument();
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { mode } = useThemeMode();
  const { t } = useTranslation('diagram');
  const { t: tDashboard } = useTranslation('dashboard');
  const { t: tc } = useTranslation('common');
  const diagramId = useMemo(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('noDiagram') === 'true') {
        return undefined;
      }
    }
    return (window as HeaderWindow).__DIAGRAM_ID__;
  }, []);
  const demoStatus = useDemoStatus();
  const authToken = useAuthStore((s) => s.token);
  const isDemoUser = demoStatus?.toUpperCase() === 'DEMO';
  const { entitlements } = useEntitlements(diagramId);
  const planClaim = useMemo(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    return getGravityUserPlan();
  }, [authToken, demoStatus]);

  const showUpgradeCta = shouldShowUpgradeCta({
    isDemoUser,
    planType: entitlements?.planType,
    legacyPlanClaim: planClaim,
  });
  const shouldShowSignupCta = isDemoUser;
  const siteUrl = useMemo(() => {
    const envUrl = (import.meta as unknown as { env?: { VITE_SITE_URL?: string } }).env?.VITE_SITE_URL;
    if (envUrl) {
      return envUrl.endsWith('/') ? envUrl : `${envUrl}/`;
    }
    if (typeof window !== 'undefined') {
      const origin = window.location.origin;
      return origin.endsWith('/') ? origin : `${origin}/`;
    }
    return '';
  }, []);

  const handleUpgradeClick = React.useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const base = siteUrl || (window.location.origin.endsWith('/') ? window.location.origin : `${window.location.origin}/`);
    window.open(`${base}plans/?app=1`, '_blank', 'noopener');
  }, [siteUrl]);

  const handleSignupClick = React.useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const signupEvent = new CustomEvent(OPEN_ONBOARDING_EVENT, { detail: { page: 'signup-in-app' } });
    window.dispatchEvent(signupEvent);
  }, []);

  // Get current selection from the diagram store
  const selectedIds = useDiagramStore((state) => state.selectedIds);
  const selectedShapeIds = useMemo(() => Array.from(selectedIds), [selectedIds]);

  const logoMarkSrc = mode === 'dark'
    ? '/assets/images/creately-logo-mark-dark.svg'
    : '/assets/images/creately-logo-mark-light.svg';

  const { data: access } = useDiagramAccess(diagramId);
  const { data: diagram } = useQuery<HeaderDiagram>({
    queryKey: ['diagram', diagramId],
    enabled: Boolean(diagramId),
    queryFn: async () => {
      const useLink = access?.source === 'link';
      const url = useLink ? `/api/link/diagrams/${diagramId}` : `/api/diagrams/${diagramId}`;
      const resp = useLink
        ? await fetch(url, { credentials: 'include' })
        : await authenticatedFetch(url);
      if (!resp.ok) throw new Error('Failed to fetch diagram');
      const j = await resp.json();
      return (j.diagram || j) as HeaderDiagram;
    },
  });

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [folderSidebarOpen, setFolderSidebarOpen] = useState(false);
  const { data: diagramInfo } = useDiagramInfo(diagramId);
  const isCompassExplorer =
    diagramInfo?.viewDefinition?.type === 'compass-explorer' ||
    diagram?.viewDefinition?.type === 'compass-explorer';


  const diagramNameToEditValue = useCallback(
    (name: string | undefined): string => {
      const raw = name?.trim() ?? '';
      if (!raw) return '';
      return getDiagramNameForDisplay(raw, t, tDashboard) as string;
    },
    [t, tDashboard],
  );

  useEffect(() => {
    setValue(diagramNameToEditValue(diagram?.name));
  }, [diagram?.name, diagramNameToEditValue]);

  const diagramTitleDisplay = useMemo(() => {
    const raw = diagram?.name?.trim() ?? '';
    if (!raw) return t('header.untitledDiagram');
    return getDiagramNameForDisplay(raw, t, tDashboard) as string;
  }, [diagram?.name, t, tDashboard]);
  const atlasDepartmentViewTitle = useAtlasDepartmentViewTitle({
    diagramId,
    folderId: diagramInfo?.viewDefinition?.sourceFolderId ?? diagramInfo?.folderId,
    viewDefinition: diagramInfo?.viewDefinition,
    canonicalTitle: diagramTitleDisplay,
  });
  const atlasInsightViewTitle = useAtlasInsightViewTitle({
    diagramId,
    folderId: diagramInfo?.viewDefinition?.sourceFolderId ?? diagramInfo?.folderId,
    viewDefinition: diagramInfo?.viewDefinition,
    canonicalTitle: diagramTitleDisplay,
  });
  const atlasContextualViewTitle = atlasDepartmentViewTitle ?? atlasInsightViewTitle;

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const renameMutation = useMutation({
    mutationFn: async (name: string) => {
      const resp = await authenticatedFetch(`/api/diagrams/${diagramId}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name })
      });
      if (!resp.ok) throw new Error('Failed to rename');
      return (await resp.json()).diagram as { id: string; name: string };
    },
    onMutate: async (name: string) => {
      await queryClient.cancelQueries({ queryKey: ['diagram', diagramId] });
      const previous = queryClient.getQueryData(['diagram', diagramId]);
      queryClient.setQueryData<HeaderDiagram | undefined>(
        ['diagram', diagramId],
        (old) => ({ ...(old || { id: diagramId ?? '', name }), name }),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['diagram', diagramId], ctx.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['diagram', diagramId] });
    }
  });

  const normalizeNameForSave = (trimmed: string): string => {
    const localizedBlank = (
      t('folderSidebar.blankDiagramDisplay', { defaultValue: 'Blank' }) as string
    ).trim();
    if (trimmed.toLowerCase() === 'blank') return 'Blank';
    if (
      trimmed === localizedBlank ||
      trimmed.toLowerCase() === localizedBlank.toLowerCase()
    ) {
      return 'Blank';
    }
    return trimmed.slice(0, 100);
  };

  const commit = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setValue(diagramNameToEditValue(diagram?.name));
      setIsEditing(false);
      return;
    }
    const stored = diagram?.name ?? '';
    const displayOfStored = stored.trim()
      ? (getDiagramNameForDisplay(stored, t, tDashboard) as string)
      : '';
    const next: string =
      stored.trim() && trimmed === displayOfStored.trim()
        ? stored
        : normalizeNameForSave(trimmed);
    if (next !== stored) renameMutation.mutate(next);
    setIsEditing(false);
  };

  const handleToggleFolderSidebar = () => {
    setFolderSidebarOpen(!folderSidebarOpen);
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div 
      className="fixed top-4 left-4 z-50 flex flex-nowrap items-center gap-2 bg-background/95 backdrop-blur-sm border rounded-lg px-4 pe-2 py-2 shadow-lg"
      style={{ top: offsetTop }}
      >

      {/* Hamburger Menu */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            data-testid="tid.editor.header.menu"
            className="p-1.5 mr-2 rounded-md hover:bg-muted transition-colors cursor-pointer"
            onClick={handleToggleFolderSidebar}
          >
            <Menu className="h-5 w-5" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{tc('accessibility.showFolderContents')}</TooltipContent>
      </Tooltip>

      {/* Creately Logo */}
      <img
        src={logoMarkSrc}
        alt={tc('app.name')}
        className="h-10 w-10 -my-2 mr-2"
      />

      {/* Breadcrumb Navigation */}
      {diagramId && (
        <DiagramBreadcrumb
          currentDiagramId={diagramId}
          currentDiagramName={diagramTitleDisplay}
          className="mr-4"
        />
      )}

      {/* Infrastructure view breadcrumbs (only renders for infra-view diagrams) */}
      {diagramId && <InfraViewBreadcrumbs diagramId={diagramId} className="mr-4" />}

      {/* Diagram Title — fixed w-56 slot so display/edit modes share the same width */}
      <div className="w-56 shrink-0">
        {atlasContextualViewTitle ? (
          <div
            data-testid={atlasDepartmentViewTitle
              ? 'tid.editor.header.departmentView'
              : 'tid.editor.header.insightView'}
            className="min-w-0"
          >
            <p
              className="truncate text-lg font-semibold text-foreground"
              aria-label={atlasContextualViewTitle.heading}
            >
              {atlasContextualViewTitle.heading}
            </p>
            <p
              className="truncate text-xs text-muted-foreground"
              title={atlasContextualViewTitle.subtitle}
            >
              {atlasContextualViewTitle.subtitle}
            </p>
          </div>
        ) : isEditing ? (
          <input
            ref={inputRef}
            data-testid="tid.editor.header.titleInput"
            value={value}
            onChange={(e) => {
              const next = e.target.value.slice(0, 100);
              setValue(next);
            }}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') {
                setValue(diagramNameToEditValue(diagram?.name));
                setIsEditing(false);
              }
            }}
            className="w-full text-lg font-semibold bg-transparent outline-none border-b border-border focus:border-foreground text-foreground"
            placeholder={t('header.untitledDiagram')}
            maxLength={100}
          />
        ) : canWrite ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                data-testid="tid.editor.header.title"
                className="group flex w-full items-center gap-1 text-left text-lg font-semibold text-foreground cursor-text bg-transparent border-none p-0"
                onClick={() => setIsEditing(true)}
                aria-label={t('header.renameAriaLabel', {
                  name: diagramTitleDisplay,
                  defaultValue: `${diagramTitleDisplay}. Click to rename.`,
                })}
              >
                <span className="min-w-0 flex-1 truncate">{diagramTitleDisplay}</span>
                <Pencil
                  className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                  aria-hidden
                />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-sm">
              <p className="font-medium">{diagramTitleDisplay}</p>
              <p className="mt-0.5 text-xs font-medium text-primary-foreground/90">
                {t('header.clickToRename', { defaultValue: 'Click to rename' })}
              </p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="block w-full truncate text-lg font-semibold text-foreground"
                aria-label={diagramTitleDisplay}
              >
                {diagramTitleDisplay}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-sm">
              {diagramTitleDisplay}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {isCompassExplorer && <CompassBreadcrumb className="mr-4" />}

      {/* Sync status indicator */}
      <SyncStatusIndicator
        isSyncing={isSyncing}
        pendingCount={pendingCount}
        saveStatus={saveStatus}
        onDownloadBackup={downloadBackup}
      />

      {/* User Avatar - uses awareness color when online, grey when offline */}
      <ConnectedUsers awareness={awareness} />

      {/* Separator */}
      <div className="relative self-stretch mx-0">
        <div className="absolute inset-y-0 left-0 w-px bg-border -my-2" />
      </div>

      {/* Action Buttons */}
      <div className="flex shrink-0 items-center gap-2">
        <ExportControls diagramId={diagramId} selectedShapeIds={selectedShapeIds} />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="default"
              size="sm"
              data-testid="tid.editor.share"
              onClick={() => setShareOpen(true)}
            >
              <Share className="h-4 w-4" />
              {t('share.title')}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('actions.shareDiagram')}</TooltipContent>
        </Tooltip>
        {showUpgradeCta ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                data-event-click="header.upgrade.click"
                data-testid="tid.editor.header.upgrade"
                variant="default"
                size="sm"
                onClick={handleUpgradeClick}
                className="bg-[#2E6BFF] hover:bg-[#2257F6]"
              >
                <Crown className="h-4 w-4" />
                {t('header.upgrade', { defaultValue: 'Upgrade' })}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{tc('accessibility.upgradeForFeatures')}</TooltipContent>
          </Tooltip>
        ) : null}
        {shouldShowSignupCta ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                data-event-click="header.signup.click"
                data-testid="tid.editor.header.signup"
                variant="default"
                size="sm"
                onClick={handleSignupClick}
                className="bg-[#2E6BFF] hover:bg-[#2257F6]"
              >
                {tc('actions.signUp', 'Sign Up')}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{tc('accessibility.signUpToSave')}</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} diagramId={diagramId} />
      </div>

      {/* Folder Sidebar */}
      <FolderSidebar
        folderId={diagram?.folderId || null}
        open={folderSidebarOpen}
        onClose={() => setFolderSidebarOpen(false)}
      />
    </TooltipProvider>
  );
};
